import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../services/api';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Chip from '@mui/material/Chip';
import { Search } from 'lucide-react';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

export default function DebitoGrant() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [filterName, setFilterName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [filterUser, setFilterUser] = useState('all');
  const [filterRole, setFilterRole] = useState('all');

  const fetchData = async () => {
    try {
      const [resGrant, resUsers, resRoles] = await Promise.all([
        api.get('/debito-grant'),
        api.get('/lorenzo-users'),
        api.get('/debito-role')
      ]);
      setList(resGrant.data);
      setUsers(resUsers.data);
      setRoles(resRoles.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterUser = (event) => {
    setFilterUser(event.target.value);
    setPage(0);
  };

  const handleFilterRole = (event) => {
    setFilterRole(event.target.value);
    setPage(0);
  };

  const filteredList = list
    .filter((item) => {
      if (filterUser !== 'all' && item.rif_user !== parseInt(filterUser)) {
        return false;
      }
      if (filterRole !== 'all' && item.rif_role !== parseInt(filterRole)) {
        return false;
      }

      if (!filterName) return true;
      const search = filterName.toLowerCase();
      const userName = `${item.name || ''} ${item.surname || ''}`.trim().toLowerCase();
      const roleTitle = (item.role_title || '').toLowerCase();
      
      return (
        item.rif_user.toString().includes(search) ||
        item.rif_role.toString().includes(search) ||
        item.id.toString().includes(search) ||
        userName.includes(search) ||
        roleTitle.includes(search)
      );
    })
    .sort((a, b) => {
      const isAsc = order === 'asc';
      
      if (orderBy === 'user_name') {
        const nameA = `${a.name || ''} ${a.surname || ''}`.trim().toLowerCase();
        const nameB = `${b.name || ''} ${b.surname || ''}`.trim().toLowerCase();
        return isAsc ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
      }
      
      if (orderBy === 'role_title') {
        const titleA = (a.role_title || '').toLowerCase();
        const titleB = (b.role_title || '').toLowerCase();
        return isAsc ? titleA.localeCompare(titleB) : titleB.localeCompare(titleA);
      }

      // All fields are numbers in this table
      if (orderBy === 'id' || orderBy === 'rif_user' || orderBy === 'rif_role') {
        return isAsc ? a[orderBy] - b[orderBy] : b[orderBy] - a[orderBy];
      }
      return 0;
    });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
          Debito Grant (Remoto)
        </Typography>
      </Box>

      <StyledCard>
        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            value={filterName}
            onChange={handleFilterByName}
            placeholder="Cerca..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 300 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Utente</InputLabel>
            <Select
              value={filterUser}
              label="Utente"
              onChange={handleFilterUser}
            >
              <MenuItem value="all">Tutti</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name} {user.surname}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Ruolo</InputLabel>
            <Select
              value={filterRole}
              label="Ruolo"
              onChange={handleFilterRole}
            >
              <MenuItem value="all">Tutti</MenuItem>
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'id'}
                    direction={orderBy === 'id' ? order : 'asc'}
                    onClick={() => handleRequestSort('id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'user_name'}
                    direction={orderBy === 'user_name' ? order : 'asc'}
                    onClick={() => handleRequestSort('user_name')}
                  >
                    User
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'role_title'}
                    direction={orderBy === 'role_title' ? order : 'asc'}
                    onClick={() => handleRequestSort('role_title')}
                  >
                    Role
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    Caricamento...
                  </TableCell>
                </TableRow>
              ) : filteredList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nessun dato trovato
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredList
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{item.id}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {item.name} {item.surname}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {item.rif_user}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">
                          {item.role_title || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {item.rif_role}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>
    </Box>
  );
}
